import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, CheckCircle, AlertCircle, Scale } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';

export function TermsOfServicePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button variant="ghost" className="mb-6" onClick={() => navigate('/')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Home
        </Button>

        <div className="space-y-8">
          <div>
            <h1 className="text-4xl font-bold mb-4">Terms of Service</h1>
            <p className="text-muted-foreground">Last updated: February 2, 2026</p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Agreement to Terms
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                By accessing or using beloud.pro's AI-powered LinkedIn growth platform, you agree to be bound by these Terms of Service.
                If you disagree with any part of the terms, you may not access the service.
              </p>
              <p>
                These terms constitute a legally binding agreement between you and beloud.pro regarding your use of our platform.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-primary" />
                Description of Service
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>beloud.pro provides the following services:</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>AI-powered content generation for LinkedIn</li>
                <li>Smart comment and reply suggestions</li>
                <li>LinkedIn profile analysis and monitoring</li>
                <li>Persona management for different brand voices</li>
                <li>Analytics and performance tracking</li>
              </ul>
              <p className="mt-4">
                We reserve the right to modify, suspend, or discontinue any part of the service at any time.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Scale className="h-5 w-5 text-primary" />
                User Obligations
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>As a user of beloud.pro, you agree to:</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>Provide accurate and complete information when creating an account</li>
                <li>Maintain the security of your account credentials</li>
                <li>Not use the service for any illegal or unauthorized purpose</li>
                <li>Not violate any laws in your jurisdiction</li>
                <li>Not use the service to generate spam, harassment, or harmful content</li>
                <li>Not attempt to interfere with the proper working of the service</li>
                <li>Comply with LinkedIn's Terms of Service when using generated content</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-primary" />
                Account Termination
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>We may terminate or suspend your account immediately, without prior notice or liability, for any reason whatsoever, including:</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>Violation of these Terms of Service</li>
                <li>Abuse of the service or other users</li>
                <li>Fraudulent or illegal activities</li>
                <li>Non-payment of fees (if applicable)</li>
                <li>Extended periods of inactivity</li>
              </ul>
              <p className="mt-4">
                Upon termination, your right to use the service will immediately cease. 
                All provisions of the Terms which by their nature should survive termination shall survive.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Payment and Refunds</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>Our payment terms are as follows:</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>Subscription fees are billed in advance on a monthly or annual basis</li>
                <li>All payments are processed securely through LemonSqueezy</li>
                <li>You may cancel your subscription at any time</li>
                <li>Refunds are provided at our discretion within 14 days of purchase</li>
                <li>Credits purchased are non-refundable but never expire</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Intellectual Property</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                The service and its original content, features, and functionality are and will remain the exclusive property 
                of beloud.pro. The service is protected by copyright, trademark, and other laws.
              </p>
              <p>
                You retain ownership of any content you generate using our platform. However, you grant us a license to use, 
                store, and process your content solely for the purpose of providing and improving our services.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Limitation of Liability</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                In no event shall beloud.pro, nor its directors, employees, partners, agents, suppliers, or affiliates,
                be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation,
                loss of profits, data, use, goodwill, or other intangible losses.
              </p>
              <p>
                We do not guarantee that the service will be secure or available at any particular time or location; 
                or that any defects or errors will be corrected.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Governing Law</CardTitle>
            </CardHeader>
            <CardContent>
              <p>
                These Terms shall be governed and construed in accordance with the laws of Italy, 
                without regard to its conflict of law provisions. Any dispute arising from these Terms 
                shall be subject to the exclusive jurisdiction of the courts of Rome, Italy.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Changes to Terms</CardTitle>
            </CardHeader>
            <CardContent>
              <p>
                We reserve the right, at our sole discretion, to modify or replace these Terms at any time. 
                We will provide notice of any significant changes by posting the new Terms on this page and 
                updating the "Last updated" date.
              </p>
            </CardContent>
          </Card>

          <div className="text-center pt-8">
            <p className="text-muted-foreground">
              Questions about the Terms? Contact us at{' '}
              <a href="mailto:admin@beloud.pro" className="text-primary hover:underline">
                admin@beloud.pro
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
